import { ContentContainer } from "@jane/lib/src/components/content/ContentContainer"
import { PageHeader } from "@jane/lib/src/components/content/PageHeader"
import { Button } from "@jane/lib/src/components/form/Button"
import { Paragraph } from "@jane/lib/src/components/Paragraph"
import { useApiCall, useApiEndpoint } from "@jane/lib/src/api";
import { config } from "../../config";
import React, {FC, useEffect, useState} from "react";
import { Breadcrumbs } from "@jane/lib/src/components/content/Breadcrumbs";
import { usePermissions } from "../../resources/PermissionsHook";
import { CategoryList } from "../../components/category-list";
import {faAward, faPlus} from "@fortawesome/free-solid-svg-icons";
import {AddLearnCategoryModal} from "../../modals/AddLearnCategoryModal";
import {
  LearnCategory, LearnModule,
  LearnSearchResult,
  LearnStatistics as LearnStatisticsType
} from "../../../../lib/src/api/repositories/learn-repository";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowTrendUp, faArrowTrendDown, faListCheck, faGaugeHigh} from "@fortawesome/pro-regular-svg-icons";
import {useToasts} from "../../../../lib/src/toasts/toasts";
import {useDebounce} from "../../util/useDebounce";
import {
  faCardsBlank,
  faChevronRight, faEllipsisVertical,
  faLayerGroup,
  faList,
  faPuzzlePiece,
} from "@fortawesome/pro-solid-svg-icons";
import {useNavigate} from "react-router-dom";
import {ModuleList} from "../../components/module-list";
import {AddLearnModuleModal} from "../../modals/AddLearnModuleModal";
import { Input } from "@jane/lib/src/components/form/Input";
import {CategoryContextMenu} from "../../components/learn/CategoryContextMenu";
import {ModuleContextMenu} from "../../components/learn/ModuleContextMenu";
import {SectionHeader} from "../../../../lib/src/components/content/SectionHeader";
import {useModal} from "@jane/lib/src/components/layout/ModalProvider";

export const LearnDashboardPage: React.FC = () => {
  const {learn} = useApiCall(config)
  const {showToast} = useToasts()
  const navigate = useNavigate()
  const { resource: categoryResource, reload: reloadCategories} = useApiEndpoint(() => learn.listCategories())
  const { resource: statistics, reload: reloadStatistics} = useApiEndpoint(() => learn.learnStatistics())
  const { resource: modules, reload: reloadModules } = useApiEndpoint(() => learn.listModules())
  const permissions = usePermissions()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 100)
  const [searchResults, setSearchResults] = useState<LearnSearchResult[]>([])

  const unassignedModules = modules?.filter((module) => module.category_id === null)
  const pinnedCategories = categoryResource?.filter((category) => category.is_pinned_category).sort((a, b) => a.name.localeCompare(b.name))
  const mainCategories = categoryResource?.filter((category) => !category.parent_category_id).sort((a, b) => a.name.localeCompare(b.name))

  const searchItemIcon = {
    'card': faCardsBlank,
    'component': faPuzzlePiece,
    'module': faLayerGroup,
    'category': faList,
  };

  const searchItemTranslations = {
    'card': 'kaarten',
    'component': 'componenten',
    'module': 'modules',
    'category': 'categorieën',
  };

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSearch('')
        setSearchResults([])
      } else if (search.length > 0 && event.key === "Enter") {
        learn.search(debouncedSearch).then(results => {
          setSearchResults(results)
        })
      }
    };

    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    }
  }, [search]);

  useEffect(() => {
    if (debouncedSearch.trim() === '') {
      return
    }
    learn.search(debouncedSearch).then(results => {
      setSearchResults(results)
    }).catch(() => {
      showToast('Error', 'Er is iets mis gegaan tijdens het zoeken')
    })
  }, [debouncedSearch]);

  const components = modules?.flatMap(r => r.components)?.filter(c => !! c) ?? []

  return <ContentContainer>
    <Breadcrumbs currentPage={'Overzicht'} crumbs={[
      {label: 'Kennisbank', href: '/learn'}
    ]} />
    <PageHeader>Kennisbank</PageHeader>

    <section className={"flex justify-between"}>
      <div className={"max-w-2xl"}>
        <Paragraph>
          De kennisbank bestaat uit onderwerpen, welke bestaan uit onderdelen. Waaronder de informatie uit het traject beschikbaar is in meerdere kaarten.<br />
          Kaarten kunnen tekst bevatten, afbeeldingen, video’s of interactieve vragen.
        </Paragraph>
      </div>
    </section>

    <div className={'mb-4 relative'}>
      <Input type={'text'} label={''} value={search} onChange={setSearch} maxWidth={'full'} placeholder={'Zoek op categorieën, modules, kaarten en meer'} onBlur={() => {
        setSearchResults([])
      }}
      onFocus={() => {
        if (search.length > 0) {
          learn.search(debouncedSearch).then(results => {
            setSearchResults(results)
          })
        }
      }}/>
      {searchResults.length > 0 && <div className={'absolute top-12 left-0 w-full bg-white shadow-lg rounded-lg flex flex-col items-stretch z-10'}>
        {searchResults.map((result, index) => <button className={'flex flex-row px-3 py-2 text-left hover:bg-brand-50 hover:cursor-pointer'} key={index} onMouseDown={(e) => {
          e.preventDefault()
          console.log(result)
          switch (result.item_type) {
            case 'card':
              const component = components.find(c => c.id === result.parent_id)
              navigate(`/learn/modules/${component?.module_id}/${component?.id}`)
              break;
            case 'component':
              navigate(`/learn/modules/${result.parent_id}/${result.item_id}`)
              break;
            case 'module':
              navigate(`/learn/modules/${result.item_id}`)
              break;
            case 'category':
              navigate(`/learn/categories/${result.item_id}`)
              break;
          }
        }
        }>
          <div className={'w-10 h-10 mr-4 flex justify-center items-center bg-brand-200 rounded-full'}>
            <FontAwesomeIcon icon={searchItemIcon[result.item_type as keyof typeof searchItemIcon]} className="text-lg text-brand-700"/>
          </div>
          <div className={'flex-col flex-1'}>
            <div className={'text-brand-900'}>{result.primary_text}</div>
            <div className={'text-xs text-slate-600 opacity-80'}>{result.secondary_text}</div>
          </div>
          <div className={'flex text-xs font-medium uppercase text-slate-600 opacity-75 items-center justify-center'}>
            {searchItemTranslations[result.item_type as keyof typeof searchItemTranslations] ?? '-'}
          </div>
          <div className={'w-10 flex justify-center items-center'}>
            <FontAwesomeIcon icon={faChevronRight} className={'text-brand-900'} />
          </div>
        </button>)}
      </div>}
    </div>

    <LearnStatistics statistics={statistics ?? null} />

    {pinnedCategories && categoryResource &&
      <PinnedCategories categories={pinnedCategories} allCategories={categoryResource} reloadCategories={reloadCategories}/>}

    {mainCategories && categoryResource &&
      <MainCategories categories={mainCategories} allCategories={categoryResource} reloadCategories={reloadCategories}/>}

    {unassignedModules && categoryResource &&
      <UnassignedModules modules={unassignedModules} categories={categoryResource} reloadModules={reloadModules} />}

  </ContentContainer>
}

const LearnStatistics: FC<{ statistics: LearnStatisticsType|null }> = (props) => {
  const navigate = useNavigate()
  return <>
    <SectionHeader>Statistieken</SectionHeader>
    <section className={'grid grid-cols-3 gap-4'}>
      <div className={'bg-white h-36 rounded-sm border border-slate-200 flex px-3 py-2'}>
        {/*Header*/}
        <div className={'flex flex-col flex-1'}>
          <div className={'flex justify-between'}>
            <div className={'text-sm'}>Uitgerolde modules</div>
            <div className={'text-[10px] text-zinc-500 my-0.5'}>in de afgelopen 7 dagen</div>
          </div>
          {/*Content*/}
          <div className={'flex flex-row my-auto items-center space-x-2'}>
            <div className={'px-4 py-4 mr-3 bg-brand-200 rounded-full'}>
              <FontAwesomeIcon icon={faListCheck} className={'text-3xl text-brand-800'} />
            </div>
            <div className={'flex-1'}>
              <div className={'flex flex-col text-3xl bold '}>
                {props.statistics != null ? <>
                  {props.statistics.deployed_modules.this_week}
                  <div className={`text-xl bold ${props.statistics.deployed_modules.this_week > props.statistics.deployed_modules.last_week ? 'text-green-600' : 'text-red-700'}`}>
                    <FontAwesomeIcon icon={props.statistics.deployed_modules.this_week > props.statistics.deployed_modules.last_week ? faArrowTrendUp : faArrowTrendDown} className={`mr-3`} />
                    <span>{Math.round(((props.statistics.deployed_modules.this_week - props.statistics.deployed_modules.last_week) / Math.max(1, props.statistics.deployed_modules.last_week)) * 100)}%</span>
                  </div>
                </> : <>

                </>}
              </div>
            </div>
            {/*<FontAwesomeIcon icon={faChevronRight} className={'justify-self-end'} />*/}
          </div>
        </div>
      </div>


      <div className={'bg-white h-36 rounded-sm border border-slate-200 flex px-3 py-2'}>
        {/*Header*/}
        <div className={'flex flex-col flex-1'}>
          <div className={'flex justify-between'}>
            <div className={'text-sm'}>Afgeronde modules</div>
            <div className={'text-[10px] text-zinc-500 my-0.5'}>in de afgelopen 7 dagen</div>
          </div>
          {/*Content*/}
          <div className={'flex flex-row my-auto items-center space-x-2'}>
            <div className={'px-4 py-4 mr-3 bg-brand-200 rounded-full'}>
              <FontAwesomeIcon icon={faListCheck} className={'text-3xl text-brand-800'} />
            </div>
            <div className={'flex-1'}>
              {props.statistics != null ? <>
                <div className={'flex flex-col text-3xl bold '}>
                  {props.statistics.finished_modules.this_week}
                </div>
                <div className={`text-xl bold ${props.statistics.finished_modules.this_week > props.statistics.finished_modules.last_week ? 'text-green-600' : 'text-red-700'}`}>
                  <FontAwesomeIcon icon={props.statistics.finished_modules.this_week > props.statistics.finished_modules.last_week ? faArrowTrendUp : faArrowTrendDown} className={`mr-3`} />
                  <span>{Math.round(((props.statistics.finished_modules.this_week - props.statistics.finished_modules.last_week) / Math.max(1, props.statistics.finished_modules.last_week)) * 100)}%</span>
                </div>
              </> : <>

              </>}
            </div>
            {/*<FontAwesomeIcon icon={faChevronRight} className={'justify-self-end'} />*/}
          </div>
        </div>
      </div>

      <div className={'bg-white h-36 rounded-sm border border-slate-200 flex px-3 py-2'}>
        {/*Header*/}
        <div className={'flex flex-col flex-1'}>
          <div className={'flex justify-between'}>
            <div className={'text-sm'}>Uitgegeven certificaten</div>
            <div className={'text-[10px] text-zinc-500 my-0.5'}>in de afgelopen 7 dagen</div>
          </div>
          {/*Content*/}
          <div className={'flex flex-row my-auto items-center space-x-2'}>
            <div className={'px-5 py-4 mr-3 bg-brand-200 rounded-full'}>
              <FontAwesomeIcon icon={faAward} className={'text-3xl text-brand-800'} />
            </div>
            <div className={'flex-1'}>
              {props.statistics != null ? <>
                <div className={'flex flex-col text-3xl bold '}>
                  {props.statistics.certificates_handed_out.this_week}
                </div>
                <div className={`text-xl bold ${props.statistics.certificates_handed_out.this_week > props.statistics.certificates_handed_out.last_week ? 'text-green-600' : 'text-red-700'}`}>
                  <FontAwesomeIcon icon={props.statistics.certificates_handed_out.this_week > props.statistics.certificates_handed_out.last_week ? faArrowTrendUp : faArrowTrendDown} className={`mr-3`} />
                  <span>{Math.round(((props.statistics.certificates_handed_out.this_week - props.statistics.certificates_handed_out.last_week) / Math.max(1, props.statistics.certificates_handed_out.last_week)) * 100)}%</span>
                </div>
              </> : <>

              </>}
            </div>
            {/*<FontAwesomeIcon icon={faChevronRight} className={'justify-self-end'} />*/}
          </div>
        </div>
      </div>

      <div>
        <Button type={'secondary'} size={'sm'} text={'Naar dashboards'} icon={faGaugeHigh} onClick={() => navigate('/dashboards-learn')} />
      </div>
    </section>
  </>
}

const PinnedCategories: FC<{categories: LearnCategory[], allCategories: LearnCategory[], reloadCategories: () => void}> = (props) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [contextMenuOpen, setContextMenuOpen] = useState(false)
  const { open: addPinnedCategoryModal } = useModal({title: "Categorie toevoegen", body: <AddLearnCategoryModal parentCategory={null} isPinned={true} onFinished={props.reloadCategories} />, size: "xl"})
  return <>
    <section className={'my-8'}>
      <div className={'flex justify-between py-2'}>
        <div className={'text-xl font-medium'}>Vastgezette categorieën</div>
        <div className={'flex space-x-4'}>
          <Button type={'secondary'} size={'xs'} text={'Toevoegen'} icon={faPlus} onClick={addPinnedCategoryModal} />
          {selectedCategories.length > 0 && <div className={'relative'}>
            <Button type={'primary'} size={'xs'} text={`Acties (${selectedCategories.length})`} icon={faEllipsisVertical} onClick={() => setContextMenuOpen(true)} />
            <CategoryContextMenu selected={selectedCategories} setSelected={setSelectedCategories} categories={props.allCategories} open={contextMenuOpen} setOpen={setContextMenuOpen} reload={props.reloadCategories} />
          </div>}
        </div>
      </div>
      <div className={''}>
        <CategoryList categories={props.categories ?? []} allCategories={props.allCategories ?? []} reload={props.reloadCategories} selected={selectedCategories} setSelected={setSelectedCategories} />
      </div>
    </section>
  </>
}

const MainCategories: FC<{ categories: LearnCategory[], allCategories: LearnCategory[], reloadCategories: () => void }> = (props) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [contextMenuOpen, setContextMenuOpen] = useState(false)
  const { open: addCategoryModal } = useModal({title: "Categorie toevoegen", body: <AddLearnCategoryModal parentCategory={null} isPinned={false} onFinished={props.reloadCategories} />, size: "xl"})
  return <>
    <section className={'my-8'}>
      <div className={'flex justify-between py-2'}>
        <div className={'text-xl font-medium'}>Alle hoofd-categorieën</div>
        <div className={'flex space-x-4'}>
          <Button type={'secondary'} size={"xs"} text={'Toevoegen'} icon={faPlus} onClick={addCategoryModal} />
          {selectedCategories.length > 0 && <div className={'relative'}>
            <Button type={'primary'} size={'xs'} text={`Acties (${selectedCategories.length})`} icon={faEllipsisVertical} onClick={() => setContextMenuOpen(true)} />
            <CategoryContextMenu selected={selectedCategories} setSelected={setSelectedCategories} categories={props.allCategories} open={contextMenuOpen} setOpen={setContextMenuOpen} reload={props.reloadCategories}/>
          </div>}
        </div>
      </div>
      <div className={''}>
        <CategoryList categories={props.categories ?? []} allCategories={props.allCategories ?? []} reload={props.reloadCategories} selected={selectedCategories} setSelected={setSelectedCategories} />
      </div>
    </section>
  </>
}

const UnassignedModules: FC<{ modules: LearnModule[], categories: LearnCategory[], reloadModules: () => void}> = (props) => {
  const [selectedModules, setSelectedModules] = useState<string[]>([])
  const [contextMenuOpen, setContextMenuOpen] = useState(false)
  const { open: addModuleModal } = useModal({title: "Module toevoegen", body: <AddLearnModuleModal parentCategory={null} onFinished={props.reloadModules} />, size: "md"})
  return <>
    <section className={'my-8'}>
      <div className={'flex justify-between py-2'}>
        <div className={'text-xl font-medium'}>Alle modules</div>
        <div className={'flex space-x-4'}>
          <Button type={'secondary'} size={"xs"} text={'Toevoegen'} icon={faPlus} onClick={addModuleModal} />
          {selectedModules.length > 0 && <div className={'relative'}>
            <Button type={'primary'} size={'xs'} text={`Acties (${selectedModules.length})`} icon={faEllipsisVertical} onClick={() => setContextMenuOpen(true)} />
            <ModuleContextMenu selected={selectedModules} setSelected={setSelectedModules} modules={props.modules} open={contextMenuOpen} setOpen={setContextMenuOpen} reload={props.reloadModules}/>
          </div>}
        </div>
      </div>
      <div className={''}>
        <ModuleList modules={props.modules} categories={props.categories} reload={props.reloadModules} selected={selectedModules} setSelected={setSelectedModules} />
      </div>
    </section>
  </>
}
